import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getLatestArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = getLatestArticles(6).filter((a) => a.id !== id).slice(0, 4);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="container-main text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://heiliaoshequ.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageHero}
        ogImageAlt={article.title}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.summary,
        image: article.imageHero,
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        url: `https://heiliaoshequ.com/article/${article.id}`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://heiliaoshequ.com/article/${article.id}`,
        },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="container-main py-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: article.category, to: `/category/${article.categorySlug}` },
              { label: article.title },
            ]} />
          </div>

          <article className="pb-14">
            <div className="container-main">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 max-w-[800px]">
                  <div className="rounded-lg overflow-hidden mb-8">
                    <img
                      src={article.imageHero}
                      alt={article.title}
                      className="w-full h-[400px] object-cover object-top"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200"
                    >
                      {article.category}
                    </Link>
                    {article.isExclusive && (
                      <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700">独家</span>
                    )}
                    {article.isHot && (
                      <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700">热门</span>
                    )}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 mt-4 pb-6 border-b border-background-200/70">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-line text-primary-500 text-sm"></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-sm font-medium text-foreground-800">
                          {article.author}
                          {article.authorVerified && (
                            <i className="ri-verified-badge-fill text-primary-400 text-sm"></i>
                          )}
                        </div>
                        <span className="text-[10px] text-foreground-400">实名认证爆料者</span>
                      </div>
                    </div>
                    <div className="w-px h-8 bg-background-200"></div>
                    <div>
                      <span className="text-sm text-foreground-600">{article.publishDate}</span>
                      <span className="block text-[10px] text-foreground-400">发布时间</span>
                    </div>
                    <div className="w-px h-8 bg-background-200"></div>
                    <div>
                      <span className="text-sm text-foreground-600">{article.views.toLocaleString()}</span>
                      <span className="block text-[10px] text-foreground-400">阅读量</span>
                    </div>
                  </div>

                  <div
                    className="mt-6 prose prose-sm max-w-none text-foreground-800 leading-relaxed
                      [&>p]:mb-4 [&>p]:text-[15px] [&>p]:leading-[1.85] [&>p]:text-foreground-800
                      [&>blockquote]:border-l-4 [&>blockquote]:border-primary-400 [&>blockquote]:pl-4 [&>blockquote]:py-2 [&>blockquote]:my-4 [&>blockquote]:bg-primary-50/50 [&>blockquote]:rounded-r-lg [&>blockquote]:text-foreground-700 [&>blockquote]:italic"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  ></div>

                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200/70">
                    <span className="text-xs text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mt-6 p-4 rounded-lg bg-background-100">
                    <span className="text-sm text-foreground-600">分享到：</span>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors duration-200" aria-label="分享到微博">
                      <i className="ri-weibo-line"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors duration-200" aria-label="分享到微信">
                      <i className="ri-wechat-line"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors duration-200" aria-label="复制链接">
                      <i className="ri-link"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors duration-200" aria-label="更多分享">
                      <i className="ri-share-forward-line"></i>
                    </button>
                  </div>
                </div>

                <aside className="w-full lg:w-80 shrink-0">
                  <div className="lg:sticky lg:top-24 space-y-6">
                    <div className="rounded-lg bg-background-100 p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-user-line text-primary-500 text-sm"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                          <p className="text-[10px] text-foreground-400">{article.authorVerified ? '实名认证' : '网名投稿'}</p>
                        </div>
                      </div>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        黑料社区认证爆料者，所发布内容已经平台真实性核验。
                      </p>
                    </div>

                    <div className="rounded-lg bg-background-100 p-5">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">相关推荐</h4>
                      <div className="space-y-3">
                        {relatedArticles.map((related) => (
                          <Link
                            key={related.id}
                            to={`/article/${related.id}`}
                            className="flex gap-3 group"
                          >
                            <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                              <img
                                src={related.imageThumb}
                                alt={related.title}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-[10px] text-foreground-400">{related.category}</span>
                              <p className="text-xs text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mt-0.5 leading-snug">
                                {related.title}
                              </p>
                              <span className="text-[10px] text-foreground-400 mt-1 block">{related.publishDate}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}